import { Box, Card, Chip, Skeleton, Tab, Tabs, Typography } from '@mui/material'
import { ReactElement } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { IBSCCourseProgress, IBSCProgressData } from '../ibsc.slice'

type Props = {
  data?: IBSCProgressData
  isLoading: boolean
}

type CourseRowProps = {
  course: IBSCCourseProgress
}

let CourseRow = (props: CourseRowProps): ReactElement => {
  let { course } = props
  let navigate = useNavigate()
  let isComplete = course.status === 'Complete'

  return (
    <Card
      variant="outlined"
      onClick={() => navigate(`/self-paced/course/${course.id}`)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        mb: 1,
        bgcolor: 'background.paperDark',
        gap: 2,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'background.paper',
        },
      }}
    >
      <Typography variant="body2" sx={{ flex: 1 }}>
        {course.name}
      </Typography>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
      >
        <Typography color="text.secondary" variant="caption">
          {course.hours} hrs
        </Typography>
        <Chip
          label={isComplete ? 'Complete' : 'Incomplete'}
          size="small"
          color={isComplete ? 'success' : 'default'}
          sx={{
            minWidth: 86,
          }}
        />
      </Box>
    </Card>
  )
}

let SkeletonList = (): ReactElement => (
  <Box>
    {[1, 2, 3, 4, 5].map((i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        sx={{
          height: 52,
          borderRadius: 1,
          mb: 1,
        }}
      />
    ))}
  </Box>
)

export default (props: Props): ReactElement => {
  let { data, isLoading } = props
  let [searchParams, setSearchParams] = useSearchParams()

  let programParam = searchParams.get('program')
  let activeProgram: 'ibsc40' | 'ibsc100' =
    programParam === 'ibsc100' ? 'ibsc100' : 'ibsc40'

  let setActiveProgram = (val: 'ibsc40' | 'ibsc100') => {
    setSearchParams({ program: val }, { replace: true })
  }

  let programData = data?.[activeProgram]
  let completedCount =
    programData?.courses.filter((c) => c.status === 'Complete').length ?? 0
  let programLabel =
    activeProgram === 'ibsc40' ? 'IBSC 40-Hour' : 'IBSC 100-Hour'

  return (
    <Box>
      <Tabs
        value={activeProgram}
        onChange={(_: React.SyntheticEvent, val: 'ibsc40' | 'ibsc100') =>
          setActiveProgram(val)
        }
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="IBSC 40" value="ibsc40" />
        <Tab label="IBSC 100" value="ibsc100" />
      </Tabs>
      {isLoading ? (
        <SkeletonList />
      ) : programData ? (
        <>
          {/* Progress summary header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="h6">{programLabel} Progress</Typography>
            <Typography color="text.secondary" variant="body2">
              {completedCount} of {programData.courses.length} courses
              {' · '}
              {programData.totalHours.toFixed(1)} / {programData.requiredHours}{' '}
              hrs
              {' · '}
              {programData.completionPercent}% complete
            </Typography>
          </Box>

          {/* Course list */}
          <Box>
            {programData.courses.map((course) => (
              <CourseRow key={course.id} course={course} />
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  )
}
